import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DeviceService } from '../../services/device-service/device.service';

interface SimSlotComparison {
  eid?: string | null;
  profile?: string | null;
  activationStatus?: string | null;
  status?: string | null;
}

interface SimTypeComparison {
  mg14?: SimSlotComparison | null;
  teal?: SimSlotComparison | null;
}

interface SimComparisonOverview {
  gatewayId?: string;
  configLastSeen?: string | null;
  esim?: SimTypeComparison | null;
  psim?: SimTypeComparison | null;
}

interface SimComparisonRow {
  label: string;
  icon: string;
  mg14: string;
  teal: string;
}

export interface TealDataUsageEntry {
  period?: string;
  usage?: number;
}

export interface TealDataUsageResponse {
  success?: boolean;
  message?: string;
  errorCode?: string;
  errors?: Array<{ code?: string; message?: string }>;
  totalCount?: number;
  entries?: TealDataUsageEntry[];
  requestId?: string;
  eid?: string;
  startDate?: string;
  endDate?: string;
  totalUsageBytes?: number;
}

@Component({
  selector: 'app-sim-details',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './sim-details.component.html',
  styleUrl: './sim-details.component.scss',
})
export class SimDetailsComponent implements OnInit, OnChanges {
  @Input() gatewayId!: string;
  @Input() eid?: string | null;

  resolvedEid: string | null = null;
  /** Which SIM type is currently active on MG14 ('eSIM' | 'pSIM' | null — shown in UI) */
  activeSimType: string | null = null;

  isLoading = false;
  lastUpdated: Date | null = null;
  comparisonRows: SimComparisonRow[] = [];
  loadError: string | null = null;

  // TEAL Data Usage state
  tealDataUsage: TealDataUsageResponse | null = null;
  tealEntries: TealDataUsageEntry[] = [];
  isTealLoading = false;
  tealLoadError: string | null = null;
  tealStartDate: string = '';
  tealEndDate: string = '';
  selectedTealPreset: 'today' | '7days' | '30days' | 'custom' = '7days';
  isExporting = false;

  constructor(
    private deviceService: DeviceService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.applyTealPreset('7days', false);
    // Data usage is fetched only AFTER sim-comparison responds and active SIM is known.
    // loadSimComparison() is triggered by ngOnChanges when gatewayId arrives.
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gatewayId']?.currentValue) {
      this.loadSimComparison();
    }
    // Note: @Input eid is intentionally NOT used to trigger data usage directly.
    // The active SIM's EID is resolved from the sim-comparison API response.
  }

  onRefresh(): void {
    this.loadSimComparison();
    if (this.resolvedEid) {
      this.loadTealDataUsage();
    }
  }

  onRefreshTealUsage(): void {
    if (this.resolvedEid) {
      this.loadTealDataUsage();
    }
  }

  onExportTealUsage(): void {
    if (!this.tealEntries || this.tealEntries.length === 0) {
      this.toastr.warning('No data usage records available to export.', 'Export');
      return;
    }

    try {
      this.isExporting = true;
      const rows = this.tealEntries.map((entry) => ({
        'Period': entry.period ? moment.utc(entry.period).format('YYYY-MM-DD HH:mm:ss') : '—',
        'Data Usage': this.formatBytes(entry.usage),
        'Usage (Bytes)': entry.usage ?? 0,
      }));

      if (this.tealDataUsage) {
        rows.push({
          'Period': 'TOTAL',
          'Data Usage': this.formatBytes(this.tealDataUsage.totalUsageBytes),
          'Usage (Bytes)': this.tealDataUsage.totalUsageBytes ?? 0,
        });
      }

      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows);

      worksheet['!cols'] = [
        { wch: 24 },
        { wch: 18 },
        { wch: 18 },
      ];

      const workbook: XLSX.WorkBook = {
        Sheets: { 'TEAL Usage': worksheet },
        SheetNames: ['TEAL Usage'],
      };

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const dateSuffix = this.tealStartDate && this.tealEndDate
        ? `${this.tealStartDate}_to_${this.tealEndDate}`
        : moment.utc().format('YYYY-MM-DD');
      const filename = `teal-data-usage-${this.resolvedEid || this.gatewayId || 'device'}_${dateSuffix}.xlsx`;

      saveAs(blob, filename);

      this.toastr.success('TEAL usage data exported successfully.', 'Export Successful', {
        timeOut: 4000,
        closeButton: true,
        progressBar: true,
      });
    } catch (err: any) {
      console.error('Failed to export TEAL usage data to Excel', err);
      this.toastr.error('Failed to export TEAL usage data to Excel.', 'Export Failed', {
        timeOut: 5000,
        closeButton: true,
        progressBar: true,
      });
    } finally {
      this.isExporting = false;
    }
  }

  get lastUpdatedLabel(): string {
    if (!this.lastUpdated) {
      return '—';
    }
    return moment.utc(this.lastUpdated).format('MMM DD, YYYY hh:mm A');
  }

  private loadSimComparison(): void {
    if (!this.gatewayId) {
      return;
    }

    this.isLoading = true;
    this.loadError = null;

    this.deviceService.getSimComparisonOverview(this.gatewayId).subscribe({
      next: (res: any) => {
        const data: SimComparisonOverview = res?.data ?? res;
        this.comparisonRows = this.buildRows(data);
        this.lastUpdated = data?.configLastSeen
          ? moment.utc(data.configLastSeen).toDate()
          : new Date();
        this.isLoading = false;

        // Determine active SIM — check MG14 status first (from mongo-config),
        // then fall back to Teal status when mongo-config returned empty.
        const esimMg14Status = data?.esim?.mg14?.status?.toLowerCase() ?? '';
        const psimMg14Status  = data?.psim?.mg14?.status?.toLowerCase() ?? '';
        const esimTealStatus  = data?.esim?.teal?.status?.toLowerCase() ?? '';
        const psimTealStatus  = data?.psim?.teal?.status?.toLowerCase() ?? '';

        // Effective status: prefer mg14 (mongo-config), fall back to teal
        const esimStatus = esimMg14Status || esimTealStatus;
        const psimStatus = psimMg14Status || psimTealStatus;

        let activeEid: string | null = null;

        if (esimStatus === 'active') {
          // eSIM is active — resolve its EID (teal preferred, mg14 as backup)
          this.activeSimType = 'eSIM';
          activeEid = this.cleanEid(
            (data as any)?.esimEid ??
            data?.esim?.teal?.eid ?? data?.esim?.mg14?.eid
          );
        } else if (psimStatus === 'active') {
          // pSIM is active — resolve its EID (teal preferred, mg14 as backup)
          this.activeSimType = 'pSIM';
          activeEid = this.cleanEid(
            (data as any)?.psimEid ??
            data?.psim?.teal?.eid ?? data?.psim?.mg14?.eid
          );
        } else {
          // Neither SIM shows as active in sim comparison — pick whichever has an EID
          this.activeSimType = null;
          activeEid = this.cleanEid(
            (data as any)?.esimEid ??
            data?.esim?.teal?.eid ?? data?.esim?.mg14?.eid ??
            (data as any)?.psimEid ??
            data?.psim?.teal?.eid ?? data?.psim?.mg14?.eid
          );
        }

        // Last resort: entire sim-comparison response was empty — use @Input eid if provided
        if (!activeEid) {
          activeEid = this.cleanEid(this.eid);
        }

        if (activeEid && activeEid !== this.resolvedEid) {
          this.resolvedEid = activeEid;
          this.loadTealDataUsage();
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.comparisonRows = this.buildRows(null);
        const msg =
          error?.error?.message ??
          error?.message ??
          'Failed to load SIM comparison data.';
        this.loadError = msg;
        this.toastr.error(msg, 'SIM Details', {
          timeOut: 8000,
          closeButton: true,
          progressBar: true,
        });

        // Fallback: mongo-config API failed — still try TEAL data usage with @Input eid if available
        const fallbackEid = this.cleanEid(this.eid);
        if (fallbackEid && fallbackEid !== this.resolvedEid) {
          this.resolvedEid = fallbackEid;
          this.loadTealDataUsage();
        }
      },
    });
  }

  applyTealPreset(preset: 'today' | '7days' | '30days', fetch = true): void {
    this.selectedTealPreset = preset;
    const today = moment.utc();
    this.tealEndDate = today.format('YYYY-MM-DD');

    if (preset === 'today') {
      this.tealStartDate = today.format('YYYY-MM-DD');
    } else if (preset === '7days') {
      this.tealStartDate = today.clone().subtract(6, 'days').format('YYYY-MM-DD');
    } else if (preset === '30days') {
      this.tealStartDate = today.clone().subtract(29, 'days').format('YYYY-MM-DD');
    }

    if (fetch && this.resolvedEid) {
      this.loadTealDataUsage();
    }
  }

  onCustomDateChange(): void {
    this.selectedTealPreset = 'custom';
  }

  onTealFilterSubmit(): void {
    if (!this.tealStartDate || !this.tealEndDate) {
      this.toastr.warning('Please select both start and end dates.', 'Date Filter');
      return;
    }

    const start = moment.utc(this.tealStartDate, 'YYYY-MM-DD');
    const end = moment.utc(this.tealEndDate, 'YYYY-MM-DD');

    if (start.isAfter(end)) {
      this.toastr.error('Start date cannot be after end date.', 'Invalid Date Range');
      return;
    }

    if (end.diff(start, 'days') >= 90) {
      this.toastr.error('Date range cannot exceed 90 days.', 'Invalid Date Range');
      return;
    }

    this.loadTealDataUsage();
  }

  loadTealDataUsage(): void {
    if (!this.resolvedEid) {
      return;
    }

    this.isTealLoading = true;
    this.tealLoadError = null;

    this.deviceService
      .getTealEsimDataUsage(this.resolvedEid, this.tealStartDate, this.tealEndDate)
      .subscribe({
        next: (res: any) => {
          this.isTealLoading = false;
          this.tealDataUsage = res;
          this.tealEntries = res?.entries ?? [];
        },
        error: (err: any) => {
          this.isTealLoading = false;
          this.tealEntries = [];
          const errorBody = err?.error;
          const msg =
            errorBody?.message ??
            err?.message ??
            'Failed to fetch TEAL eSIM data usage.';
          this.tealLoadError = msg;
          this.toastr.error(msg, 'TEAL Data Usage', {
            timeOut: 8000,
            closeButton: true,
            progressBar: true,
          });
        },
      });
  }

  formatBytes(bytes: number | null | undefined): string {
    if (bytes == null || isNaN(bytes) || bytes === 0) {
      return '0 B';
    }
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const val = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return `${val} ${sizes[i]}`;
  }

  formatPeriodDate(period: string | null | undefined): string {
    if (!period) return '—';
    const m = moment.utc(period);
    return m.isValid() ? m.format('MMM DD, YYYY') : period;
  }

  private cleanEid(val: unknown): string | null {
    if (val == null) return null;
    const str = String(val).trim();
    return str && str !== '0' && str !== '—' && str.toLowerCase() !== 'null' ? str : null;
  }

  private buildRows(data: SimComparisonOverview | null): SimComparisonRow[] {
    return [
      this.createRow('eSIM Profile', 'sim_card', data?.esim?.mg14, data?.esim?.teal, 'profile'),
      this.createRow(
        'eSIM Activation Status',
        'check_circle',
        data?.esim?.mg14,
        data?.esim?.teal,
        'activationStatus'
      ),
      this.createRow('eSIM Status', 'signal_cellular_alt', data?.esim?.mg14, data?.esim?.teal, 'status'),
      this.createRow('pSIM Profile', 'sd_card', data?.psim?.mg14, data?.psim?.teal, 'profile'),
      this.createRow(
        'pSIM Activation Status',
        'check_circle',
        data?.psim?.mg14,
        data?.psim?.teal,
        'activationStatus'
      ),
      this.createRow('pSIM Status', 'signal_cellular_alt', data?.psim?.mg14, data?.psim?.teal, 'status'),
    ];
  }

  private createRow(
    label: string,
    icon: string,
    mg14: SimSlotComparison | null | undefined,
    teal: SimSlotComparison | null | undefined,
    field: keyof SimSlotComparison
  ): SimComparisonRow {
    return {
      label,
      icon,
      mg14: this.displayValue(mg14?.[field]),
      teal: this.displayValue(teal?.[field]),
    };
  }

  private displayValue(value: string | null | undefined): string {
    if (value === null || value === undefined || `${value}`.trim() === '') {
      return '—';
    }
    return `${value}`.trim();
  }
}
